// src/Pages/Jobs.jsx
import React, {
  useState, useEffect, useMemo, useCallback
} from 'react';
import Sidebar from '../Components/Sidebar';
import '../Style/Jobs.css';
import { useNavigate } from 'react-router-dom';
import {
  FaEye, FaEdit, FaTrashAlt, FaRegFileAlt,
  FaPlay, FaCheckCircle, FaExclamationTriangle,
  FaBug, FaFlask, FaFilePdf, FaTimes, FaSearch,
  FaChevronDown, FaCopy, FaClipboardList, FaTruck, FaCube
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db } from '../firebaseConfig';
import {
  collection, getDocs, deleteDoc, doc, query
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import CompanyLogo from '../assets/CompanyLogo.png';

/* ───────── helpers ───────── */
const fmtDate = d =>
  !d ? '—'
    : typeof d === 'string' ? d.slice(0, 10)
      : d?.toDate ? d.toDate().toISOString().slice(0, 10)
        : d instanceof Date ? d.toISOString().slice(0, 10)
          : '—';

const toDataUrl = async url => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise(ok => {
      const fr = new FileReader();
      fr.onload = () => ok(fr.result);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const loadImgDims = (dataUrl) =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = dataUrl;
  });

const debounce = (func, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => func(...args), wait);
  };
};

const uniq = arr => Array.from(new Set(arr.filter(Boolean)));

/* Status meta styling (extend with Trips statuses) */
const statusMeta = {
  Draft: { icon: <FaRegFileAlt />, color: '#777' },
  Pending: { icon: <FaRegFileAlt />, color: '#777' },
  'In Progress': { icon: <FaPlay />, color: '#d89614' },
  'On Hold': { icon: <FaExclamationTriangle />, color: '#d89614' },
  Completed: { icon: <FaCheckCircle />, color: '#18a058' },
  Overdue: { icon: <FaExclamationTriangle />, color: '#111' },
  Cancelled: { icon: <FaTrashAlt />, color: '#111' },
  'Order received': { icon: <FaCheckCircle />, color: '#18a058' },
  'Order pending': { icon: <FaRegFileAlt />, color: '#777' },
  // Trips statuses
  Scheduled: { icon: <FaPlay />, color: '#00a8e8' },
  Done: { icon: <FaCheckCircle />, color: '#18a058' }
};

/* ========= Normalization Helpers for Tests ========= */
const entryToPair = ent => {
  if (!ent) return [null, null];
  if (Array.isArray(ent)) return [ent[0], ent[1]];
  if (typeof ent === 'object') {
    if ('label' in ent) {
      if ('value' in ent) return [ent.label, ent.value];
      if ('ok' in ent) return [ent.label, ent.ok];
    }
    if ('field' in ent) {
      return [ent.field, ent.value];
    }
    if ('name' in ent && 'url' in ent) {
      return [ent.name, ent.url];
    }
  }
  return [null, null];
};

const extractACTest = doc => {
  if (!doc.acSections || !Array.isArray(doc.acSections)) return null;
  const flatten = {};
  doc.acSections.forEach(sec => {
    (sec.data || []).forEach(item => {
      const [k, v] = entryToPair(item);
      if (k) flatten[k] = v;
    });
  });
  const summaryCandidates = [
    ['IR1000V', flatten.IR1000V],
    ['Eff%', flatten.EfficiencyPct],
    ['BearingDE', flatten.BearingDE],
    ['VibDE', flatten.VibrationDE]
  ].filter(([, v]) => v !== undefined && v !== '');
  return {
    key: 'ac',
    label: 'AC Test',
    summary: summaryCandidates.slice(0, 4),
    raw: flatten
  };
};

const extractFinalInspection = doc => {
  const fi = doc.finalInspection;
  if (!fi) return null;

  const list = fi.finalInspectionItems || fi.checklist || [];
  const passed = list.filter(i => i.passed).length;
  const total = list.length;

  let checkedBy = '—';
  if (Array.isArray(fi.signOff)) {
    const obj = fi.signOff.find(s => (s.field || s[0]) === 'CheckedBy');
    if (obj) {
      checkedBy = obj.value ?? obj[1] ?? '—';
    }
  }

  return {
    key: 'fi',
    label: 'Final Inspection',
    summary: [
      ['Description', fi.description],
      ['Passed', `${passed}/${total}`],
      ['CheckedBy', checkedBy]
    ],
    raw: {
      description: fi.description,
      items: list,
      signOff: fi.signOff || []
    }
  };
};

const extractCylinder = doc => {
  if (!doc.cylinder) return null;
  const cyl = doc.cylinder;
  const identity = {};
  (cyl.identity || []).forEach(ent => {
    const [k, v] = entryToPair(ent);
    if (k) identity[k] = v;
  });
  const results = {};
  (cyl.results || []).forEach(ent => {
    const [k, v] = entryToPair(ent);
    if (k) results[k] = v;
  });
  const lowOk = (cyl.cylinderChecksLow || []).filter(c => c.ok).length;
  const highOk = (cyl.cylinderChecksHigh || []).filter(c => c.ok).length;
  const summary = [
    ['OpenCentres', identity.OpenCentres],
    ['ClosedCentres', identity.ClosedCentres],
    ['Final', results.FinalResult],
    ['LowOK', `${lowOk}/${(cyl.cylinderChecksLow || []).length}`]
  ].filter(([, v]) => v !== undefined && v !== '');
  return {
    key: 'cyl',
    label: 'Cylinder',
    summary: summary.slice(0, 4),
    raw: {
      identity,
      results,
      low: cyl.cylinderChecksLow,
      high: cyl.cylinderChecksHigh,
      lowOk,
      highOk
    }
  };
};

const extractDrifter = doc => {
  if (!doc.drifter) return null;
  const d = doc.drifter;
  const idObj = {};
  (d.idSection || []).forEach(ent => {
    const [k, v] = entryToPair(ent);
    if (k) idObj[k] = v;
  });
  const temps = {};
  (d.temperatures || []).forEach(ent => {
    const [k, v] = entryToPair(ent);
    if (k) temps[k] = v;
  });
  let checkedBy = '—';
  if (Array.isArray(d.signOff) && d.signOff.length) {
    const [k, v] = entryToPair(d.signOff[0]);
    checkedBy = v || '—';
  }
  const summary = [
    ['OilΔ', temps.OilBefore && temps.OilAfter ? `${temps.OilBefore}→${temps.OilAfter}` : '—'],
    ['PercTested', (d.percussion || d.drifterPercussion || []).find(r => r.label?.toLowerCase().includes('percussion tested'))?.ok ? 'YES' : 'NO'],
    ['RotTested', (d.rotation || d.drifterRotation || []).find(r => r.label?.toLowerCase().includes('rotation tested'))?.ok ? 'YES' : 'NO'],
    ['CheckedBy', checkedBy]
  ];
  return {
    key: 'drf',
    label: 'Drifter',
    summary,
    raw: {
      idObj,
      temps,
      percussion: d.percussion || d.drifterPercussion,
      rotation: d.rotation || d.drifterRotation,
      notes: d.shiftNotes || d.drifterShiftNotes,
      checkedBy
    }
  };
};

const gatherTestSections = doc => {
  const arr = [];
  const ac = extractACTest(doc); if (ac) arr.push(ac);
  const fi = extractFinalInspection(doc); if (fi) arr.push(fi);
  const cyl = extractCylinder(doc); if (cyl) arr.push(cyl);
  const drf = extractDrifter(doc); if (drf) arr.push(drf);
  return arr;
};

/* ========= Image / Attachment URL gathering ========= */
const extFromUrl = (u) => {
  try {
    const clean = u.split('?')[0].split('#')[0];
    const m = clean.match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toLowerCase() : '';
  } catch { return ''; }
};
const looksLikeImageUrl = (u) =>
  typeof u === 'string' && /^https?:/i.test(u) &&
  /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(u);
const looksLikePdfUrl = (u) =>
  typeof u === 'string' && /^https?:/i.test(u) &&
  /\.pdf(\?.*)?$/i.test(u);
const looksLikeCsvUrl = (u) =>
  typeof u === 'string' && /^https?:/i.test(u) &&
  /\.csv(\?.*)?$/i.test(u);

// deep collector
const deepCollectLinks = (root) => {
  const out = new Set();
  const seen = new WeakSet();
  const walk = (val) => {
    if (val == null) return;
    const t = typeof val;
    if (t === 'string') {
      if (/^https?:/i.test(val)) out.add(val);
      return;
    }
    if (t !== 'object') return;
    if (seen.has(val)) return;
    seen.add(val);

    ['url', 'downloadURL', 'src', 'href'].forEach(k => {
      if (typeof val[k] === 'string' && /^https?:/i.test(val[k])) out.add(val[k]);
    });

    if (Array.isArray(val)) {
      val.forEach(walk);
      return;
    }
    for (const k in val) {
      try { walk(val[k]); } catch { /* no-op */ }
    }
  };
  walk(root);
  return Array.from(out);
};

const collectImageUrls = (obj) => {
  if (!obj || typeof obj !== 'object') return [];
  const urls = [];

  if (obj.imageUrl && looksLikeImageUrl(obj.imageUrl)) urls.push(obj.imageUrl);

  if (Array.isArray(obj.fileMeta)) {
    obj.fileMeta.forEach(x => {
      const url = x?.url || x?.downloadURL;
      const kind = x?.kind || x?.type || x?.contentType;
      if (url && (String(kind).startsWith('image') || looksLikeImageUrl(url))) urls.push(url);
    });
  }
  if (Array.isArray(obj.fileUrls)) {
    obj.fileUrls.forEach(u => { if (looksLikeImageUrl(u)) urls.push(u); });
  }
  ['images', 'photos', 'pics', 'attachments', 'gallery'].forEach(k => {
    if (Array.isArray(obj[k])) obj[k].forEach(u => { if (looksLikeImageUrl(u)) urls.push(u); });
  });

  const deep = deepCollectLinks(obj).filter(looksLikeImageUrl);
  return uniq([...urls, ...deep]);
};

const collectAttachments = (obj) => {
  const images = collectImageUrls(obj);

  // documents (pdf/csv/other)
  let docs = [];
  const pushDoc = (name, url, kind) => docs.push({ name: name || url, url, kind });

  if (Array.isArray(obj.fileMeta)) {
    obj.fileMeta.forEach(x => {
      const url = x?.url || x?.downloadURL;
      if (!url || looksLikeImageUrl(url)) return;
      const k = (x.kind || x.type || '').toLowerCase();
      const kind = k.includes('pdf') ? 'pdf' : k.includes('csv') ? 'csv' : (k || (looksLikePdfUrl(url) ? 'pdf' : looksLikeCsvUrl(url) ? 'csv' : 'other'));
      pushDoc(x.name, url, kind);
    });
  }
  if (Array.isArray(obj.fileUrls)) {
    obj.fileUrls.forEach(u => {
      if (!u || looksLikeImageUrl(u)) return;
      const ext = extFromUrl(u);
      const kind = ext === 'pdf' ? 'pdf' : ext === 'csv' ? 'csv' : 'other';
      pushDoc(undefined, u, kind);
    });
  }
  deepCollectLinks(obj).forEach(u => {
    if (looksLikeImageUrl(u)) return;
    if (looksLikePdfUrl(u)) pushDoc(undefined, u, 'pdf');
    else if (looksLikeCsvUrl(u)) pushDoc(undefined, u, 'csv');
  });

  const seen = new Set();
  docs = docs.filter(d => {
    if (seen.has(d.url)) return false;
    seen.add(d.url);
    return true;
  });

  return { images, docs };
};

/* ========= PDF Helpers ========= */
const pdfHeader = async (pdf, pageW, yStart) => {
  let y = yStart;
  const logo64 = await toDataUrl(CompanyLogo);
  const logoSize = 48;
  if (logo64) {
    pdf.addImage(logo64, undefined, (pageW - logoSize) / 2, y, logoSize, logoSize);
  }
  y += logoSize + 12;
  pdf.setFont('Helvetica', 'bold').setFontSize(15).setTextColor('#111');
  pdf.text('NYABANGA ENGINEERING', pageW / 2, y, { align: 'center' });
  y += 14;
  pdf.setFont('Helvetica', 'normal').setFontSize(8).setTextColor('#000');
  [
    'Manufacture & Repairs of Hydraulic Cylinders & Drive Train Components',
    'Plot 35, Pretoria Road, Fairlands, 1500, Benoni',
    '011 420-1537  |  082 471-9512  |  info@nyabanga.co.za',
    'Vat Reg: 4610201685  |  Reg: 200206044423'
  ].forEach(line => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 9; });
  return y + 4;
};

const pdfKeyValueTable = (pdf, startY, title, pairs) => {
  if (!pairs.length) return startY;
  pdf.setFont('Helvetica', 'bold').setFontSize(11).setTextColor('#111');
  pdf.text(title, 40, startY);
  const body = pairs.map(([k, v]) => [k, v ?? '—']);
  autoTable(pdf, {
    startY: startY + 4,
    head: [['Field', 'Value']],
    body,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [0, 0, 0], textColor: 255 }
  });
  return pdf.lastAutoTable.finalY + 10;
};

/* Standalone image grid used by some PDFs */
const addImagesGrid = async (pdf, startY, title, imgUrls) => {
  const urls = uniq(imgUrls);
  if (!urls.length) return startY;

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 40;
  const marginBottom = 40;
  let y = startY;

  pdf.setFont('Helvetica', 'bold').setFontSize(11).setTextColor('#111');
  pdf.text(title, marginX, y);
  y += 8;

  const maxW = pageW - marginX * 2;
  const perRow = 3;
  const gap = 8;
  const cellW = (maxW - gap * (perRow - 1)) / perRow;
  const cellH = 120;

  let col = 0;

  for (let i = 0; i < urls.length; i++) {
    const u64 = await toDataUrl(urls[i]);
    if (!u64) continue;
    const { w, h } = await loadImgDims(u64);
    const ratio = w && h ? Math.min(cellW / w, cellH / h) : 1;
    const drawW = Math.max(1, Math.min(cellW, Math.round((w || cellW) * ratio)));
    const drawH = Math.max(1, Math.min(cellH, Math.round((h || cellH) * ratio)));

    if (y + drawH > pageH - marginBottom) {
      pdf.addPage();
      y = 40;
    }

    const x = marginX + col * (cellW + gap) + (cellW - drawW) / 2;
    pdf.addImage(u64, undefined, x, y, drawW, drawH);

    col++;
    if (col >= perRow) {
      col = 0;
      y += cellH + gap;
    }
  }

  if (col !== 0) y += cellH + gap;
  return y;
};

/** Compute a good min height for an image grid INSIDE A TABLE CELL */
const computeCellImageHeight = (count, cellWidth) => {
  if (!count) return 24;
  const perRow = 3;
  const gap = 6;
  const thumbW = (cellWidth - gap * (perRow - 1)) / perRow;
  const thumbH = 80;
  const rows = Math.ceil(count / perRow);
  return rows * thumbH + (rows - 1) * gap + 8;
};

/** Draw thumbnails inside a given autoTable cell rectangle */
const drawThumbsInCell = (pdf, dataUrls, cell) => {
  const perRow = 3;
  const gap = 6;
  const thumbH = 80;

  const usableW = cell.width;
  const thumbW = (usableW - gap * (perRow - 1)) / perRow;
  const x0 = cell.x;
  let y = cell.y + 4;

  for (let i = 0; i < dataUrls.length; i++) {
    const col = i % perRow;
    const row = Math.floor(i / perRow);
    const x = x0 + col * (thumbW + gap);
    const yy = y + row * (thumbH + gap);
    try {
      pdf.addImage(dataUrls[i], undefined, x, yy, thumbW, thumbH);
    } catch {
      // ignore bad images
    }
  }
};

/* ═════════════════ component ═════════════════ */
export default function Jobs() {
  const [active, setActive] = useState('jobs');
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState([]);
  const [fails, setFails] = useState([]);
  const [tests, setTests] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [trips, setTrips] = useState([]);
  const [products, setProducts] = useState([]); // NEW: products

  const [statusFilter, setStatusFilter] = useState(null);
  const [dialog, setDialog] = useState({ open: false, type: '', docId: '' });
  const [pdfSelect, setPdfSelect] = useState({ open: false, doc: null, sections: [] });
  const [view, setView] = useState({ open: false, doc: null, sections: [] });

  // Bulk selection for products
  const [selectedProducts, setSelectedProducts] = useState({}); // {docId: true}

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /* fetch data on mount */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const jSnap = await getDocs(query(collection(db, 'Jobs')));
        const jArr = jSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) =>
            parseInt((b.jobId || '0').replace(/\D/g, ''), 10) -
            parseInt((a.jobId || '0').replace(/\D/g, ''), 10)
          );
        setJobs(jArr);

        const fSnap = await getDocs(collection(db, 'FailureReports'));
        const fArr = fSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) =>
            parseInt((b.reportId || '0').replace(/\D/g, ''), 10) -
            parseInt((a.reportId || '0').replace(/\D/g, ''), 10)
          );
        setFails(fArr);

        const tSnap = await getDocs(collection(db, 'TestReports'));
        const tArr = tSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) =>
            parseInt((b.reportId || '0').replace(/\D/g, ''), 10) -
            parseInt((a.reportId || '0').replace(/\D/g, ''), 10)
          );
        setTests(tArr);

        const rSnap = await getDocs(collection(db, 'RFQs'));
        const rArr = rSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) =>
            parseInt((b.rfqNo || '0').replace(/\D/g, ''), 10) -
            parseInt((a.rfqNo || '0').replace(/\D/g, ''), 10)
          );
        setRfqs(rArr);

        const trSnap = await getDocs(collection(db, 'Trips'));
        const trArr = trSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) => {
            const getNum = v => parseInt(String(v || '0').replace(/\D/g, ''), 10);
            return getNum(b.tripNo || b.tripId || b.docId) - getNum(a.tripNo || a.tripId || a.docId);
          });
        setTrips(trArr);

        // NEW: Products
        const pSnap = await getDocs(collection(db, 'Products'));
        const pArr = pSnap.docs.map(d => ({ docId: d.id, ...d.data() }))
          .sort((a, b) => {
            const getNum = v => parseInt(String(v || '0').replace(/\D/g, ''), 10);
            return getNum(b.productId || b.docId) - getNum(a.productId || a.docId);
          });
        setProducts(pArr);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch data.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ESC close modals */
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') {
        if (dialog.open) setDialog({ open: false, type: '', docId: '' });
        if (pdfSelect.open) setPdfSelect({ open: false, doc: null, sections: [] });
        if (view.open)   setView({ open: false, doc: null, sections: [] });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dialog, pdfSelect, view]);

  const handleSearch = useCallback(
    debounce(value => setSearch(value), 300),
    []
  );

  const collPath = {
    jobs: 'Jobs',
    failure: 'FailureReports',
    tests: 'TestReports',
    rfqs: 'RFQs',
    trips: 'Trips',
    products: 'Products'
  }[active];

  const source = {
    jobs,
    failure: fails,
    tests,
    rfqs,
    trips,
    products
  }[active];

  /* Normalized status getter for filtering/counting/badges */
  const getStatus = (rec) => {
    if (active === 'trips') return rec.tripStatus || rec.status || '—';
    if (active === 'tests') return rec.header?.status || rec.status || '—';
    // products & rfqs/failure/jobs default
    return rec.status || rec.header?.status || '—';
  };

  const filtered = useMemo(() => {
    return source
      .filter(r => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
      .filter(r => {
        if (active === 'products') return true; // no status filter for products
        const st = getStatus(r);
        return statusFilter ? st === statusFilter : true;
      });
  }, [source, search, statusFilter, active]);

  /* CRUD */
  const handleDelete = async id => {
    try {
      await deleteDoc(doc(db, collPath, id));
      if (active === 'jobs') setJobs(p => p.filter(x => x.docId !== id));
      if (active === 'failure') setFails(p => p.filter(x => x.docId !== id));
      if (active === 'tests') setTests(p => p.filter(x => x.docId !== id));
      if (active === 'rfqs') setRfqs(p => p.filter(x => x.docId !== id));
      if (active === 'trips') setTrips(p => p.filter(x => x.docId !== id));
      if (active === 'products') setProducts(p => p.filter(x => x.docId !== id));
      toast.success('Deleted successfully!', { className: 'toast-success' });
    } catch {
      toast.error('Failed to delete.', { className: 'toast-error' });
    }
    setDialog({ open: false, type: '', docId: '' });
  };

  const handleEdit = id => {
    setDialog({ open: false, type: '', docId: '' });
    if (active === 'jobs') navigate(`/jobs/edit/${id}`);
    if (active === 'failure') navigate(`/failure/edit/${id}`);
    if (active === 'tests') navigate(`/test/edit/${id}`);
    if (active === 'rfqs') navigate(`/rfq/edit/${id}`);
    if (active === 'trips') navigate(`/CollectionAndDelivery/edit/${id}`);
    if (active === 'products') navigate(`/products/edit/${id}`);
  };

  const openDialog = (type, id) => setDialog({ open: true, type, docId: id });
  const closeDialog = () => setDialog({ open: false, type: '', docId: '' });

  /* View modal */
  const openView = itm => {
    if (active === 'tests') {
      setView({ open: true, doc: itm, sections: gatherTestSections(itm) });
    } else {
      setView({ open: true, doc: itm, sections: [] });
    }
  };
  const closeView = () => setView({ open: false, doc: null, sections: [] });

  const copyJSON = () => {
    if (!view.doc) return;
    const clean = { ...view.doc };
    delete clean.fileUrls;
    delete clean.docs;
    try {
      navigator.clipboard.writeText(JSON.stringify(clean, null, 2));
      toast.success('JSON copied!', { className: 'toast-success' });
    } catch {
      toast.error('Copy failed.', { className: 'toast-error' });
    }
  };

  const copySummary = () => {
    if (!view.doc) return;
    let txt = '';
    if (active === 'jobs') {
      txt = `Job ${view.doc.jobId}
Customer: ${view.doc.customer || '—'}
Status: ${getStatus(view.doc) || '—'}
Date: ${fmtDate(view.doc.createdAt)}
RFQ: ${view.doc.linkedRfqNo || view.doc.linkedRfqId || '—'}
Name: ${view.doc.jobName || '—'}
Description: ${view.doc.description || '—'}`;
    } else if (active === 'failure') {
      txt = `Failure Report ${view.doc.reportId}
Name: ${view.doc.name || view.doc.title || view.doc.equipment || '—'}
Job: ${view.doc.jobId}
Customer: ${view.doc.customer || '—'}
Equipment: ${view.doc.equipment || '—'}
Serial: ${view.doc.serial || '—'}
Date: ${fmtDate(view.doc.createdAt)}
Description: ${view.doc.description || '—'}`;
    } else if (active === 'tests') {
      const header = view.doc.header || {};
      txt = `Test Report ${view.doc.reportId}
Customer: ${header.customer || view.doc.customer || '—'}
Job: ${header.jobId || view.doc.jobId || '—'}
Date: ${fmtDate(header.date || view.doc.createdAt)}
Status: ${getStatus(view.doc)}`;
      if (view.sections.length) {
        txt += '\nSections:\n' + view.sections.map(s => ` - ${s.label}`).join('\n');
      }
    } else if (active === 'rfqs') {
      txt = `RFQ ${view.doc.rfqNo || view.doc.rfqId || view.doc.docId}
Customer: ${view.doc.customer || '—'}
Status: ${getStatus(view.doc)}
Date: ${fmtDate(view.doc.rfqDate || view.doc.createdAt)}
Expiry: ${fmtDate(view.doc.expiryDate)}
Payment Terms: ${view.doc.paymentTerms || '—'}
Delivery Terms: ${view.doc.deliveryTerms || '—'}`;
    } else if (active === 'trips') {
      const drv = view.doc.driver?.name || view.doc.driverName || '—';
      const veh = view.doc.vehicle?.numberPlate || view.doc.vehicleReg || view.doc.vehicle?.name || '—';
      txt = `Trip ${view.doc.tripNo || view.doc.tripId || view.doc.docId}
Type: ${view.doc.tripType || view.doc.type || view.doc.category || '—'}
Customer: ${view.doc.customer || view.doc.assignment?.customer || '—'}
Date: ${fmtDate(view.doc.tripDate || view.doc.date || view.doc.scheduledDate || view.doc.createdAt)}
Driver: ${drv}
Vehicle: ${veh}
Status: ${getStatus(view.doc)}
Total: R${Number(view.doc.tripCost || 0).toFixed(2)} (Base R${Number(view.doc.tripCostBase || 0).toFixed(2)}${view.doc.sleepover ? ` + Overnight R${Number(view.doc.sleepoverExtra || 0).toFixed(0)}` : ''})`;
    } else if (active === 'products') {
      txt = `Product ${view.doc.productId}
In-House: ${view.doc.inHousePart || '—'}
Name: ${view.doc.name || '—'}
Date: ${fmtDate(view.doc.dateCreated)}
OEM: ${view.doc.oemPart || '—'}
Qty: ${Number(view.doc.quantity || 0)}
Cost: R${Number(view.doc.costPrice || 0).toFixed(2)}
Markup %: ${Number(view.doc.markupPct || 0).toFixed(2)}
Selling (Unit): R${Number(view.doc.sellingPrice || view.doc.unitAfterProfit || 0).toFixed(2)}`;
    }
    try {
      navigator.clipboard.writeText(txt);
      toast.success('Summary copied!', { className: 'toast-success' });
    } catch {
      toast.error('Copy failed.', { className: 'toast-error' });
    }
  };

  /* Test PDF section selector */
  const openTestPdfSelector = docObj => {
    const sections = gatherTestSections(docObj);
    if (!sections.length) {
      toast.error('No test data found in this report.', { className: 'toast-error' });
      return;
    }
    setPdfSelect({ open: true, doc: docObj, sections });
  };

  const generatePDFForTestSection = async (docObj, key) => {
    setPdfSelect({ open: false, doc: null, sections: [] });
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    let y = await pdfHeader(pdf, pageW, 32);

    const titleMap = {
      ac: 'AC TEST REPORT',
      fi: 'FINAL INSPECTION REPORT',
      cyl: 'CYLINDER TEST REPORT',
      drf: 'DRIFTER TEST REPORT'
    };
    pdf.setFont('Helvetica', 'bold').setFontSize(13).text(titleMap[key] || 'TEST REPORT', 40, y);
    y += 18;

    autoTable(pdf, {
      startY: y,
      head: [['REPORT ID', 'CUSTOMER', 'JOB NO.', 'STATUS', 'DATE']],
      body: [[
        docObj.reportId || '—',
        docObj.header?.customer || docObj.customer || '—',
        docObj.header?.jobId || docObj.jobId || '—',
        docObj.header?.status || docObj.status || '—',
        fmtDate(docObj.header?.date || docObj.createdAt)
      ]],
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [0, 0, 0], textColor: 255 }
    });
    y = pdf.lastAutoTable.finalY + 10;

    const pdfKeyValue = (title, pairs) => {
      if (!pairs.length) return;
      y = pdfKeyValueTable(pdf, y, title, pairs);
    };

    if (key === 'ac') {
      (docObj.acSections || []).forEach(sec => {
        const rows = (sec.data || []).map(item => {
          const [k, v] = entryToPair(item);
          return [k, v || '—'];
        }).filter(r => r[0]);
        pdfKeyValue(sec.section, rows);
      });
    } else if (key === 'fi') {
      const fi = extractFinalInspection(docObj);
      if (fi) {
        const compRows = (fi.raw.items || []).map(i => [i.label, i.passed ? 'PASS' : 'FAIL']);
        pdfKeyValue('Inspection Items', compRows);

        const signRows = (fi.raw.signOff || []).map(s => {
          if (Array.isArray(s)) return [s[0], s[1]];
          if (s.field) return [s.field, s.value];
          return entryToPair(s);
        }).filter(r => r[0]);
        pdfKeyValue('Sign Off', signRows);
      }
    } else if (key === 'cyl') {
      const cyl = extractCylinder(docObj);
      if (cyl) {
        pdfKeyValue('Identification',
          Object.entries(cyl.raw.identity).filter(([k]) => !['SerialNo','RodDia','MaxPressure'].includes(k))
        );
        pdfKeyValue('Low Pressure Checks', (cyl.raw.low || []).map(c => [c.label, c.ok ? 'OK' : 'X']));
        pdfKeyValue('High Pressure Checks', (cyl.raw.high || []).map(c => [c.label, c.ok ? 'OK' : 'X']));
        pdfKeyValue('Results',
          Object.entries(cyl.raw.results).filter(([k]) => !['MaxPressure'].includes(k))
        );
      }
    } else if (key === 'drf') {
      const drf = extractDrifter(docObj);
      if (drf) {
        pdfKeyValue('Identification',
          Object.entries(drf.raw.idObj).filter(([k]) =>
            !['DrifterType','SerialNo','TestingPressure'].includes(k)
          )
        );
        pdfKeyValue('Temperatures', Object.entries(drf.raw.temps));
        pdfKeyValue('Percussion', (drf.raw.percussion || []).map(r => {
          if (r.ok !== undefined) return [r.label, r.ok ? 'YES' : 'NO'];
          const [k, v] = entryToPair(r);
          return [k, v || '—'];
        }));
        pdfKeyValue('Rotation', (drf.raw.rotation || []).map(r => {
          if (r.ok !== undefined) return [r.label, r.ok ? 'YES' : 'NO'];
          const [k, v] = entryToPair(r);
          return [k, v || '—'];
        }));
        if (drf.raw.notes?.length) {
          pdfKeyValue('Shift Notes', drf.raw.notes.map(n => [n]));
        }
        pdfKeyValue('Sign Off', [['CheckedBy', drf.raw.checkedBy]]);
      }
    }

    const imgs = collectImageUrls(docObj);
    y = await addImagesGrid(pdf, y, 'Images', imgs);

    pdf.save(`${titleMap[key] || 'Test'}_${docObj.reportId || docObj.docId}.pdf`);
    toast.success('PDF generated.', { className: 'toast-success' });
  };

  /* Generic PDF (jobs/failure/rfqs/trips) — simplified for these types */
  const generateGenericPDF = async (itm, type) => {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    let y = await pdfHeader(pdf, pageW, 32);
    const TITLE =
      type === 'failure' ? 'FAILURE REPORT'
        : type === 'jobs' ? 'JOB REPORT'
          : type === 'rfqs' ? 'RFQ'
            : type === 'trips' ? 'COLLECTION & DELIVERY'
              : type === 'products' ? 'PRODUCT' : 'REPORT';

    pdf.setFont('Helvetica', 'bold').setFontSize(13).text(TITLE, 40, y);
    y += 18;

    if (type === 'failure') {
      autoTable(pdf, {
        startY: y,
        head: [['CUSTOMER', 'DATE', 'EQUIPMENT', 'JOB NO.', 'SERIAL', 'STATUS']],
        body: [[
          itm.customer || '—',
          fmtDate(itm.createdAt),
          itm.equipment || '—',
          itm.jobId || '—',
          itm.serial || '—',
          itm.status || '—'
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      const nameVal = itm.name || itm.title || itm.equipment || '—';
      const descVal = itm.description || itm.notes || '—';
      const imgUrls = collectImageUrls(itm);
      const dataUrls = (await Promise.all(imgUrls.map(toDataUrl))).filter(Boolean);
      const tableWidth = pageW - 80;
      const colW = { name: 0.25 * tableWidth, desc: 0.45 * tableWidth, imgs: 0.30 * tableWidth };
      const imgsMinHeight = computeCellImageHeight(Math.min(dataUrls.length, 12), colW.imgs);

      autoTable(pdf, {
        startY: y,
        head: [['NAME', 'DESCRIPTION', 'IMAGES']],
        body: [[{ content: nameVal }, { content: descVal }, { content: '' }]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 6, overflow: 'linebreak', valign: 'top' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 },
        columnStyles: { 0: { cellWidth: colW.name }, 1: { cellWidth: colW.desc }, 2: { cellWidth: colW.imgs, minCellHeight: imgsMinHeight } },
        didParseCell: data => {
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.minCellHeight = imgsMinHeight;
          }
        },
        didDrawCell: data => {
          if (data.section === 'body' && data.column.index === 2 && dataUrls.length) {
            drawThumbsInCell(pdf, dataUrls.slice(0, 12), data.cell);
          }
        }
      });
      y = pdf.lastAutoTable.finalY + 10;
    } else if (type === 'rfqs') {
      autoTable(pdf, {
        startY: y,
        head: [['RFQ NO', 'CUSTOMER', 'STATUS', 'DATE', 'EXPIRY']],
        body: [[
          itm.rfqNo || itm.rfqId || itm.docId,
          itm.customer || '—',
          itm.status || '—',
          fmtDate(itm.rfqDate || itm.createdAt),
          fmtDate(itm.expiryDate)
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      const details = [
        ['Payment Terms', itm.paymentTerms || '—'],
        ['Delivery Terms', itm.deliveryTerms || '—'],
        ['Customer Ref', itm.customerRef || '—'],
        ['Notes', itm.notes || '—']
      ];
      y = pdfKeyValueTable(pdf, y, 'Details', details);
    } else if (type === 'trips') {
      autoTable(pdf, {
        startY: y,
        head: [['TRIP NO', 'TYPE', 'CUSTOMER', 'DATE', 'STATUS']],
        body: [[
          itm.tripNo || itm.tripId || itm.docId,
          itm.type || itm.tripType || itm.category || '—',
          itm.customer || itm.assignment?.customer || '—',
          fmtDate(itm.tripDate || itm.date || itm.scheduledDate || itm.createdAt),
          itm.tripStatus || itm.status || '—'
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      const drv = itm.driver?.name || itm.driverName || '—';
      const drvLic = itm.driver?.licenseNumber || '—';
      const veh = itm.vehicle?.name || '—';
      const vehReg = itm.vehicle?.numberPlate || itm.vehicleReg || '—';
      const details = [
        ['From', itm.origin || itm.pickupAddress || itm.from || '—'],
        ['To', itm.destination || itm.deliveryAddress || itm.to || '—'],
        ['Driver', drv],
        ['Driver License', drvLic],
        ['Vehicle', veh],
        ['Vehicle Reg', vehReg],
        ['Reference', itm.reference || itm.customerRef || '—'],
        ['Notes', itm.notes || '—']
      ];
      y = pdfKeyValueTable(pdf, y, 'Trip Details', details);

      const costRows = [
        ['Template', itm.tripCostTemplateId || '—'],
        ['Base Amount (ZAR)', `R${Number(itm.tripCostBase || 0).toFixed(2)}`],
        ['Overnight', itm.sleepover ? 'YES' : 'NO'],
        ['Overnight Extra', itm.sleepover ? `R${Number(itm.sleepoverExtra || 0).toFixed(2)}` : 'R0.00'],
        ['Total Trip Cost', `R${Number(itm.tripCost || 0).toFixed(2)}`]
      ];
      y = pdfKeyValueTable(pdf, y, 'Costing', costRows);

      if (itm.assignment) {
        const a = itm.assignment;
        const aRows = [
          ['From Collection', a.from || '—'],
          ['ID', a.id || '—'],
          ['Job/RFQ', a.jobId || a.rfqNo || a.rfqId || '—'],
          ['Customer', a.customer || '—'],
          ['Name/Desc', a.jobName || a.description || '—']
        ];
        y = pdfKeyValueTable(pdf, y, 'Linked Assignment', aRows);
      }
    } else if (type === 'products') {
      // Product header
      autoTable(pdf, {
        startY: y,
        head: [['PRODUCT ID', 'IN-HOUSE', 'NAME', 'DATE', 'OEM']],
        body: [[
          itm.productId || itm.docId,
          itm.inHousePart || '—',
          itm.name || '—',
          fmtDate(itm.dateCreated || itm.createdAt),
          itm.oemPart || '—'
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      // Pricing / Qty
      const unitBefore = Number(itm.unitBeforeProfit ?? itm.costPrice ?? 0);
      const unitAfter  = Number(itm.unitAfterProfit ?? itm.sellingPrice ?? 0);
      const qty        = Number(itm.quantity ?? 0);
      const pricing = [
        ['Quantity', String(qty)],
        ['Cost Price (Unit)', `R${Number(itm.costPrice || 0).toFixed(2)}`],
        ['Mark-up %', `${Number(itm.markupPct || 0).toFixed(2)}%`],
      ];
      y = pdfKeyValueTable(pdf, y, 'Inventory & Pricing', pricing);

      // Description
      y = pdfKeyValueTable(pdf, y, 'Description', [['Notes', itm.description || '—']]);

      // Image (if any)
      const imgs = collectImageUrls(itm);
      y = await addImagesGrid(pdf, y, 'Image', imgs.slice(0, 3));
    } else { // jobs
      autoTable(pdf, {
        startY: y,
        head: [['JOB ID', 'JOB NAME', 'STATUS', 'CUSTOMER', 'DATE', 'RFQ']],
        body: [[
          itm.jobId || '—',
          itm.jobName || '—',
          itm.status || '—',
          itm.customer || '—',
          fmtDate(itm.createdAt),
          itm.linkedRfqNo || itm.linkedRfqId || '—'
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      const details = [
        ['Description', itm.description || '—'],
        ['Notes', itm.notes || '—']
      ];
      y = pdfKeyValueTable(pdf, y, 'Details', details);
    }

    pdf.save(`${TITLE}_${itm.reportId || itm.jobId || itm.rfqNo || itm.tripNo || itm.productId || itm.docId}.pdf`);
    toast.success('PDF generated.', { className: 'toast-success' });
  };

  /* Bulk Products PDF */
  const generateBulkProductsPDF = async (list) => {
    if (!list.length) {
      toast.error('No products selected.', { className: 'toast-error' });
      return;
    }
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });

    for (let idx = 0; idx < list.length; idx++) {
      if (idx > 0) pdf.addPage();
      const itm = list[idx];
      const pageW = pdf.internal.pageSize.getWidth();
      let y = await pdfHeader(pdf, pageW, 32);

      pdf.setFont('Helvetica', 'bold').setFontSize(13).text('PRODUCT', 40, y);
      y += 18;

      autoTable(pdf, {
        startY: y,
        head: [['PRODUCT ID', 'IN-HOUSE', 'NAME', 'DATE', 'OEM']],
        body: [[
          itm.productId || itm.docId,
          itm.inHousePart || '—',
          itm.name || '—',
          fmtDate(itm.dateCreated || itm.createdAt),
          itm.oemPart || '—'
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 0, 0], textColor: 255 }
      });
      y = pdf.lastAutoTable.finalY + 10;

      const unitBefore = Number(itm.unitBeforeProfit ?? itm.costPrice ?? 0);
      const unitAfter  = Number(itm.unitAfterProfit ?? itm.sellingPrice ?? 0);
      const qty        = Number(itm.quantity ?? 0);
      const pricing = [
        ['Quantity', String(qty)],
        ['Cost Price (Unit)', `R${Number(itm.costPrice || 0).toFixed(2)}`],
        ['Mark-up %', `${Number(itm.markupPct || 0).toFixed(2)}%`],
      ];
      y = pdfKeyValueTable(pdf, y, 'Inventory & Pricing', pricing);
      y = pdfKeyValueTable(pdf, y, 'Description', [['Notes', itm.description || '—']]);

      const imgs = collectImageUrls(itm);
      y = await addImagesGrid(pdf, y, 'Image', imgs.slice(0, 3));
    }

    pdf.save(`Products_${list.length}_items.pdf`);
    toast.success('Bulk PDF generated.', { className: 'toast-success' });
  };

  const onGeneratePdf = itm => {
    if (active === 'tests') openTestPdfSelector(itm);
    else generateGenericPDF(itm, active);
  };

  /* ---------- CARD BODY ---------- */
  const CardBody = itm => {
    const id = itm.reportId || itm.jobId || itm.rfqNo || itm.rfqId || itm.tripNo || itm.tripId || itm.productId || itm.docId;
    const statusVal = getStatus(itm);

    if (active === 'jobs') {
      return (
        <>
          <h2 className="id-heading">Job {id}</h2>
          <p className="line"><strong>Job Name:</strong> {itm.jobName || '—'}</p>
          <p className="line"><strong>Customer:</strong> {itm.customer || '—'}</p>
          <p className="line"><strong>Date:</strong> {fmtDate(itm.createdAt)}</p>
          <p className="line"><strong>RFQ:</strong> {itm.linkedRfqNo || itm.linkedRfqId || '—'}</p>
          <p className="line"><strong>Status:</strong> {statusVal || '—'}</p>
        </>
      );
    }

    if (active === 'failure') {
      return (
        <>
          <h2 className="id-heading">FR {id}</h2>
          <p className="line"><strong>Name:</strong> {itm.name || itm.title || itm.equipment || '—'}</p>
          <p className="line"><strong>Job:</strong> {itm.jobId || '—'}</p>
          <p className="line"><strong>Customer:</strong> {itm.customer || '—'}</p>
          <p className="line"><strong>Date:</strong> {fmtDate(itm.createdAt)}</p>
          <p className="line"><strong>Status:</strong> {statusVal || '—'}</p>
          <p className="line"><strong>Description:</strong> {itm.description || itm.notes || '—'}</p>
        </>
      );
    }

    if (active === 'rfqs') {
      return (
        <>
          <h2 className="id-heading">RFQ {itm.rfqNo || itm.rfqId || itm.docId}</h2>
          <p className="line"><strong>Customer:</strong> {itm.customer || '—'}</p>
          <p className="line"><strong>Date:</strong> {fmtDate(itm.rfqDate || itm.createdAt)}</p>
          <p className="line"><strong>Expiry:</strong> {fmtDate(itm.expiryDate)}</p>
          <p className="line"><strong>Status:</strong> {statusVal || '—'}</p>
          {itm.paymentTerms && <p className="line"><strong>Payment:</strong> {itm.paymentTerms}</p>}
          {itm.deliveryTerms && <p className="line"><strong>Delivery:</strong> {itm.deliveryTerms}</p>}
        </>
      );
    }

    if (active === 'trips') {
      const type = itm.type || itm.tripType || itm.category || '—';
      const date = fmtDate(itm.tripDate || itm.date || itm.scheduledDate || itm.createdAt);
      const drv = itm.driver?.name || itm.driverName || '—';
      const veh = itm.vehicle?.numberPlate || itm.vehicleReg || itm.vehicle?.name || '—';
      return (
        <>
          <h2 className="id-heading">Trip {id}</h2>
          <p className="line"><strong>Type:</strong> {type}</p>
          <p className="line"><strong>Customer:</strong> {itm.customer || itm.assignment?.customer || '—'}</p>
          <p className="line"><strong>Date:</strong> {date}</p>
          <p className="line"><strong>Driver:</strong> {drv}</p>
          <p className="line"><strong>Vehicle:</strong> {veh}</p>
          <p className="line"><strong>Total:</strong> R{Number(itm.tripCost || 0).toFixed(2)}</p>
          <p className="line"><strong>Status:</strong> {statusVal || '—'}</p>
        </>
      );
    }

    if (active === 'products') {
      const unitAfter = Number(itm.unitAfterProfit ?? itm.sellingPrice ?? 0);
      const unitBefore = Number(itm.unitBeforeProfit ?? itm.costPrice ?? 0);
      return (
        <>
          <h2 className="id-heading">Product {id}</h2>
          <p className="line"><strong>In-House:</strong> {itm.inHousePart || '—'}</p>
          <p className="line"><strong>Name:</strong> {itm.name || '—'}</p>
          <p className="line"><strong>Date:</strong> {fmtDate(itm.dateCreated || itm.createdAt)}</p>
          <p className="line"><strong>OEM:</strong> {itm.oemPart || '—'}</p>
          <p className="line"><strong>Qty:</strong> {Number(itm.quantity || 0)}</p>
        </>
      );
    }

    // TEST REPORT CARD
    const header = itm.header || {};
    const jobNo = header.jobId || itm.jobId || '—';
    const customer = header.customer || itm.customer || '—';
    const date = fmtDate(header.date || itm.createdAt);
    const status = header.status || itm.status || '—';
    let testType = itm.reportType || 'TEST';
    const sections = gatherTestSections(itm);
    if (sections.length === 1) testType = sections[0].label;
    else if (sections.length > 1) testType = 'Multiple';

    return (
      <div className="test-card-compact">
        <h2 className="id-heading">TR {itm.reportId || itm.docId}</h2>
        <p className="line"><strong>Job:</strong> {jobNo}</p>
        <p className="line"><strong>Customer:</strong> {customer}</p>
        <p className="line"><strong>Date:</strong> {date}</p>
        <p className="line"><strong>Type:</strong> {testType}</p>
        <p className="line"><strong>Status:</strong> {status}</p>
      </div>
    );
  };

  /* Loading skeleton */
  const Skeleton = () => (
    <div className="card-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div className="mini-card skeleton" key={i}>
          <div className="shimmer" />
        </div>
      ))}
    </div>
  );

  /* Status badge utility */
  const statusBadge = (stat) => {
    const meta = statusMeta[stat] || {};
    const clr = meta.color || '#555';
    return (
      <span
        className="status-badge"
        style={{
          background: clr + '22',
          color: clr,
          border: '1px solid ' + clr + '55'
        }}
      >
        {stat || '—'}
      </span>
    );
  };

  /* Attachments preview component for the View modal */
  const AttachmentsPreview = ({ record }) => {
    const { images, docs } = collectAttachments(record);
    if (!images.length && !docs.length) return null;

    return (
      <div className="attachments-section" style={{ marginTop: 16 }}>
        <h4 style={{ margin: '8px 0' }}>Attachments</h4>

        {!!images.length && (
          <>
            <div className="files-grid" style={{ marginBottom: 12 }}>
              {images.map((u, i) => (
                <div key={i} className="file-chip">
                  <div className="preview image">
                    <img src={u} alt={`attachment-${i}`} />
                  </div>
                  <div className="fname" title={u}>Image {i + 1}</div>
                  <a className="remove-chip" href={u} target="_blank" rel="noreferrer" aria-label="Open image">
                    <FaEye />
                  </a>
                </div>
              ))}
            </div>
          </>
        )}

        {!!docs.length && (
          <div className="docs-wrap" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            {docs.map((d, i) => (
              <div key={i} className="doc-preview card" style={{ padding: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {d.kind === 'pdf' ? <FaFilePdf /> : <FaRegFileAlt />}
                    <span style={{ fontWeight: 600 }}>{d.name || `Document ${i + 1}`}</span>
                    <span className="status-badge" style={{ marginLeft: 8 }}>{(d.kind || 'file').toUpperCase()}</span>
                  </div>
                  <a className="btn-outline" href={d.url} target="_blank" rel="noreferrer">Open</a>
                </div>
                {d.kind === 'pdf' && (
                  <iframe
                    title={d.name || `pdf-${i}`}
                    src={d.url}
                    style={{ width: '100%', height: 300, border: '1px solid #eaeaea', borderRadius: 6 }}
                  />
                )}
                {d.kind === 'csv' && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    CSV preview may not render inline. Use <a href={d.url} target="_blank" rel="noreferrer">Open</a> to view/download.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* Product selection helpers */
  const toggleProductSelection = (docId) => {
    setSelectedProducts(prev => ({ ...prev, [docId]: !prev[docId] }));
  };
  const clearSelection = () => setSelectedProducts({});

  const selectedList = useMemo(
    () => products.filter(p => selectedProducts[p.docId]),
    [products, selectedProducts]
  );

  return (
    <div className="jobs-page">
      <ToastContainer position="top-right" autoClose={2500} />
      <Sidebar />

      <main className="jobs-main">
        <nav className="jobs-tabs" role="tablist">
          {[
            { key: 'jobs', label: ' Jobs', icon: <FaRegFileAlt /> },
            { key: 'rfqs', label: ' RFQs', icon: <FaClipboardList /> },
            { key: 'trips', label: ' Collection & Deliveries', icon: <FaTruck /> },
            { key: 'failure', label: ' Failure Reports', icon: <FaBug /> },
            { key: 'tests', label: ' Test Reports', icon: <FaFlask /> },
            { key: 'products', label: ' Products', icon: <FaCube /> } // NEW
          ].map(t => (
            <button
              key={t.key}
              className={active === t.key ? 'tab active' : 'tab'}
              onClick={() => {
                setActive(t.key);
                setSearch('');
                setStatusFilter(null);
                if (t.key !== 'products') clearSelection();
              }}
              role="tab"
              aria-selected={active === t.key}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </nav>

        <div className="top-bar">
          <div className="search-wrap">
            <FaSearch className="icon" />
            <input
              className="search-input"
              placeholder={`Search ${active}…`}
              defaultValue={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>

          {/* Bulk PDF actions for products */}
          {active === 'products' && (
            <div className="bulk-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span className="muted small">
                Selected: <b>{selectedList.length}</b>
              </span>
              <button
                className="btn-outline"
                onClick={() => generateBulkProductsPDF(selectedList)}
                disabled={!selectedList.length}
                title="Generate one PDF with all selected products"
              >
                <FaFilePdf /> Bulk PDF
              </button>
              {selectedList.length > 0 && (
                <button className="btn-clear-filter" onClick={clearSelection}>
                  <FaTimes /> Clear
                </button>
              )}
            </div>
          )}

          {/* Status filter reset for other tabs */}
          {statusFilter && active !== 'products' && (
            <button className="btn-clear-filter" onClick={() => setStatusFilter(null)}>
              <FaTimes /> Clear Filter
            </button>
          )}
        </div>

        {/* Hide status cards when in products */}
        {active !== 'products' && (
          <div className="status-cards-row">
            {Object.entries(statusMeta).map(([type, meta]) => {
              const total = source.filter(d => getStatus(d) === type).length;
              if (!total) return null;
              return (
                <div
                  key={type}
                  className={`status-card-small ${statusFilter === type ? 'active' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === type ? null : type)}
                  role="button"
                  style={{ '--status-color': meta.color }}
                >
                  <div className="status-icon-small" style={{ backgroundColor: meta.color }}>
                    {meta.icon}
                  </div>
                  <span className="status-type">{type}</span>
                  <span className="status-count">{total}</span>
                </div>
              );
            })}
          </div>
        )}

        {loading ? (
          <Skeleton />
        ) : filtered.length ? (
          <div className="card-grid">
            {filtered.map(itm => {
              const stat = getStatus(itm);
              const isProduct = active === 'products';
              const img = isProduct ? (collectImageUrls(itm)[0] || null) : null;

              return (
                <div key={itm.docId} className="mini-card">
                  {/* Tiny product image preview in header (if available) */}
                  {isProduct && img && (
                    <div className="mini-thumb">
                   
                    </div>
                  )}

                  <div className="mini-details">
                    <CardBody {...itm} />

                    {/* Status line hidden for products (no status) */}
                    {active !== 'products' && (
                      <div className="status-line">
                        {statusBadge(stat)}
                      </div>
                    )}

                    <div className="action-row">
                      <button
                        className="btn-pdf"
                        onClick={() => onGeneratePdf(itm)}
                      >
                        <FaFilePdf /> PDF
                      </button>

                      {/* Product selection checkbox */}
                      {isProduct && (
                        <label className="chk-wrap" style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={!!selectedProducts[itm.docId]}
                            onChange={() => toggleProductSelection(itm.docId)}
                          />
                          <span className="muted small">Select</span>
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="mini-actions">
                    <button onClick={() => openView(itm)} aria-label="View">
                      <FaEye />
                    </button>
                    <button onClick={() => openDialog('edit', itm.docId)} aria-label="Edit">
                      <FaEdit />
                    </button>
                    {/* allow delete for all including products */}
                    <button onClick={() => openDialog('delete', itm.docId)} aria-label="Delete">
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-results">
            <p>
              No {active} match your search
              {statusFilter ? ` (status: ${statusFilter})` : ''}.
              {statusFilter && active !== 'products' && (
                <button
                  className="btn-reset-filter-inline"
                  onClick={() => setStatusFilter(null)}
                >
                  Reset Filter
                </button>
              )}
            </p>
          </div>
        )}

        {/* Confirm Dialog */}
        {dialog.open && (
          <div className="dialog-overlay" role="dialog">
            <div className="dialog">
              <h3>{dialog.type === 'edit' ? 'Confirm Edit' : 'Confirm Delete'}</h3>
              <p>
                {dialog.type === 'edit'
                  ? 'Edit this record?'
                  : 'Delete this record permanently?'}
              </p>
              <div className="dialog-actions">
                <button className="btn-cancel" onClick={closeDialog}>Cancel</button>
                <button
                  className={dialog.type === 'edit' ? 'btn-confirm' : 'btn-delete'}
                  onClick={() =>
                    dialog.type === 'edit'
                      ? handleEdit(dialog.docId)
                      : handleDelete(dialog.docId)
                  }
                >
                  {dialog.type === 'edit' ? 'Edit' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Test PDF Selection Modal */}
        {pdfSelect.open && (
          <div className="dialog-overlay" role="dialog">
            <div className="dialog pdf-select">
              <h3>Select Test Section</h3>
              <p className="muted">
                Report ID: <strong>{pdfSelect.doc.reportId}</strong>
              </p>
              <div className="pdf-options">
                {pdfSelect.sections.map(s => (
                  <button
                    key={s.key}
                    className="pdf-option"
                    onClick={() => generatePDFForTestSection(pdfSelect.doc, s.key)}
                  >
                    {s.label} <FaChevronDown className="chev" />
                    <div className="opt-summary">
                      {(s.summary || []).map(([k, v]) => (
                        <span key={k}><b>{k}:</b> {v || '—'}</span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
              <div className="dialog-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setPdfSelect({ open: false, doc: null, sections: [] })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {view.open && (
          <div className="dialog-overlay" role="dialog">
            <div className="dialog view-dialog">
              <h3>Details</h3>
              <p className="muted small">Basic information for quick reference.</p>
              <div className="view-grid">
                {active === 'jobs' && (
                  <>
                    <div><b>Job ID:</b> {view.doc.jobId}</div>
                    <div><b>Name:</b> {view.doc.jobName || '—'}</div>
                    <div><b>Status:</b> {getStatus(view.doc)}</div>
                    <div><b>Customer:</b> {view.doc.customer || '—'}</div>
                    <div><b>Date:</b> {fmtDate(view.doc.createdAt)}</div>
                    <div><b>RFQ:</b> {view.doc.linkedRfqNo || view.doc.linkedRfqId || '—'}</div>
                    <div className="full"><b>Description:</b> {view.doc.description || '—'}</div>
                  </>
                )}
                {active === 'failure' && (
                  <>
                    <div><b>Report ID:</b> {view.doc.reportId}</div>
                    <div><b>Name:</b> {view.doc.name || view.doc.title || view.doc.equipment || '—'}</div>
                    <div><b>Job No.:</b> {view.doc.jobId}</div>
                    <div><b>Customer:</b> {view.doc.customer || '—'}</div>
                    <div><b>Equipment:</b> {view.doc.equipment || '—'}</div>
                    <div><b>Serial:</b> {view.doc.serial || '—'}</div>
                    <div><b>Date:</b> {fmtDate(view.doc.createdAt)}</div>
                    <div><b>Status:</b> {getStatus(view.doc)}</div>
                    <div className="full"><b>Description:</b> {view.doc.description || view.doc.notes || '—'}</div>
                  </>
                )}
                {active === 'rfqs' && (
                  <>
                    <div><b>RFQ No.:</b> {view.doc.rfqNo || view.doc.rfqId || view.doc.docId}</div>
                    <div><b>Customer:</b> {view.doc.customer || '—'}</div>
                    <div><b>Status:</b> {getStatus(view.doc)}</div>
                    <div><b>Date:</b> {fmtDate(view.doc.rfqDate || view.doc.createdAt)}</div>
                    <div><b>Expiry:</b> {fmtDate(view.doc.expiryDate)}</div>
                    {view.doc.paymentTerms && <div><b>Payment Terms:</b> {view.doc.paymentTerms}</div>}
                    {view.doc.deliveryTerms && <div><b>Delivery Terms:</b> {view.doc.deliveryTerms}</div>}
                    {view.doc.customerRef && <div><b>Customer Ref:</b> {view.doc.customerRef}</div>}
                    {view.doc.notes && <div className="full"><b>Notes:</b> {view.doc.notes}</div>}
                  </>
                )}
                {active === 'trips' && (
                  <>
                    <div><b>Trip No.:</b> {view.doc.tripNo || view.doc.tripId || view.doc.docId}</div>
                    <div><b>Type:</b> {view.doc.type || view.doc.tripType || view.doc.category || '—'}</div>
                    <div><b>Customer:</b> {view.doc.customer || view.doc.assignment?.customer || '—'}</div>
                    <div><b>Status:</b> {getStatus(view.doc)}</div>
                    <div><b>Date:</b> {fmtDate(view.doc.tripDate || view.doc.date || view.doc.scheduledDate || view.doc.createdAt)}</div>
                    <div><b>From:</b> {view.doc.origin || view.doc.pickupAddress || view.doc.from || '—'}</div>
                    <div><b>To:</b> {view.doc.destination || view.doc.deliveryAddress || view.doc.to || '—'}</div>
                    <div><b>Driver:</b> {view.doc.driver?.name || view.doc.driverName || '—'}</div>
                    <div><b>Driver License:</b> {view.doc.driver?.licenseNumber || '—'}</div>
                    <div><b>Vehicle:</b> {view.doc.vehicle?.name || '—'}</div>
                    <div><b>Vehicle Reg:</b> {view.doc.vehicle?.numberPlate || view.doc.vehicleReg || '—'}</div>
                    <div><b>Total:</b> R{Number(view.doc.tripCost || 0).toFixed(2)}</div>
                    <div><b>Base Amount:</b> R{Number(view.doc.tripCostBase || 0).toFixed(2)}</div>
                    <div><b>Overnight:</b> {view.doc.sleepover ? `YES (+R${Number(view.doc.sleepoverExtra || 0).toFixed(0)})` : 'NO'}</div>
                    {view.doc.assignment && (
                      <div className="full">
                        <b>Linked Assignment:</b>{' '}
                        {(view.doc.assignment.from || '—')}{' '}
                        {(view.doc.assignment.jobId || view.doc.assignment.rfqNo || view.doc.assignment.rfqId || '')}
                      </div>
                    )}
                    {view.doc.reference && <div className="full"><b>Reference:</b> {view.doc.reference}</div>}
                    {view.doc.notes && <div className="full"><b>Notes:</b> {view.doc.notes}</div>}
                  </>
                )}
                {active === 'tests' && (
                  <>
                    <div><b>Report ID:</b> {view.doc.reportId}</div>
                    <div><b>Job No.:</b> {view.doc.header?.jobId || view.doc.jobId || '—'}</div>
                    <div><b>Customer:</b> {view.doc.header?.customer || view.doc.customer || '—'}</div>
                    <div><b>Status:</b> {getStatus(view.doc)}</div>
                    <div><b>Date:</b> {fmtDate(view.doc.header?.date || view.doc.createdAt)}</div>
                    <div className="full">
                      <b>Sections:</b>{' '}
                      {view.sections.length
                        ? view.sections.map(s => s.label).join(', ')
                        : '—'}
                    </div>
                    {view.sections
                      .filter(s => s.key !== 'fi')
                      .map(s => (
                        <div key={s.key} className="sub-summary full">
                          <div className="sub-title">{s.label}</div>
                          <div className="sub-items">
                            {(s.summary || []).map(([k, v]) => (
                              <span key={k}><b>{k}:</b> {v || '—'}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </>
                )}
                {active === 'products' && (
                  <>
                    <div><b>Product ID:</b> {view.doc.productId || view.doc.docId}</div>
                    <div><b>In-House Part:</b> {view.doc.inHousePart || '—'}</div>
                    <div><b>Name:</b> {view.doc.name || '—'}</div>
                    <div><b>Date Created:</b> {fmtDate(view.doc.dateCreated || view.doc.createdAt)}</div>
                    <div><b>OEM Part No.:</b> {view.doc.oemPart || '—'}</div>
                    <div><b>Quantity:</b> {Number(view.doc.quantity || 0)}</div>
                    <div><b>Cost Price:</b> R{Number(view.doc.costPrice || 0).toFixed(2)}</div>
                    <div><b>Mark-up %:</b> {Number(view.doc.markupPct || 0).toFixed(2)}%</div>
                    <div className="full"><b>Description:</b> {view.doc.description || '—'}</div>
                  </>
                )}
              </div>

              {/* Attachments preview (images & documents) */}
              <AttachmentsPreview record={view.doc} />

              <div className="dialog-actions">
                <button className="btn-secondary" onClick={copySummary}>
                  <FaCopy /> Copy Summary
                </button>
                <button className="btn-secondary" onClick={copyJSON}>
                  <FaCopy /> Copy JSON
                </button>
                <button className="btn-cancel" onClick={closeView}>Close</button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
